import { useState, useEffect } from 'react';
import { MessageSquare, Key, User, Send, CheckCircle, AlertCircle, Smartphone, Shield, UserCheck, UserX } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';

interface SMSConfig {
  provider: string;
  accessKeyId: string;
  accessKeySecret: string;
  signName: string;
  verifyCodeTemplate: string;
  teacherApproveTemplate: string;
  teacherRejectTemplate: string;
}

const defaultConfig: SMSConfig = {
  provider: 'aliyun',
  accessKeyId: '',
  accessKeySecret: '',
  signName: '',
  verifyCodeTemplate: 'SMS_506425088',
  teacherApproveTemplate: 'SMS_506060098',
  teacherRejectTemplate: 'SMS_506065098',
};

export default function SMSSettings() {
  const [config, setConfig] = useState<SMSConfig>(defaultConfig);
  const [testPhone, setTestPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    const { data } = await supabase.from('sms_settings').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle();
    if (data) {
      setConfig({
        provider: data.provider || defaultConfig.provider,
        accessKeyId: data.access_key_id || '',
        accessKeySecret: data.access_key_secret || '',
        signName: data.sign_name || '',
        verifyCodeTemplate: data.verify_code_template || defaultConfig.verifyCodeTemplate,
        teacherApproveTemplate: data.teacher_approve_template || defaultConfig.teacherApproveTemplate,
        teacherRejectTemplate: data.teacher_reject_template || defaultConfig.teacherRejectTemplate,
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('sms_settings').upsert({
      provider: config.provider,
      access_key_id: config.accessKeyId,
      access_key_secret: config.accessKeySecret,
      sign_name: config.signName,
      verify_code_template: config.verifyCodeTemplate,
      teacher_approve_template: config.teacherApproveTemplate,
      teacher_reject_template: config.teacherRejectTemplate,
      updated_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) {
      setMessage({ type: 'error', text: '保存失败' });
    } else {
      setMessage({ type: 'success', text: '配置保存成功' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleTest = async () => {
    if (!testPhone) {
      setMessage({ type: 'error', text: '请输入测试手机号' });
      return;
    }
    setTesting(true);
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/send-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: testPhone,
          templateCode: config.verifyCodeTemplate,
          templateParam: { code: '123456' },
        }),
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: `验证码短信已发送至 ${testPhone}` });
      } else {
        setMessage({ type: 'error', text: result.error || '发送失败' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '发送失败，请检查配置' });
    }
    setTesting(false);
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          短信服务配置
        </h2>

        {message && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">服务商</label>
            <select
              value={config.provider}
              onChange={(e) => setConfig({ ...config, provider: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="aliyun">阿里云短信</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">AccessKey ID</label>
            <div className="relative">
              <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={config.accessKeyId}
                onChange={(e) => setConfig({ ...config, accessKeyId: e.target.value })}
                className="w-full pl-9 pr-3 py-2 border rounded-lg"
                placeholder="阿里云 AccessKey ID"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">AccessKey Secret</label>
            <div className="relative">
              <Key className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={config.accessKeySecret}
                onChange={(e) => setConfig({ ...config, accessKeySecret: e.target.value })}
                className="w-full pl-9 pr-3 py-2 border rounded-lg"
                placeholder="阿里云 AccessKey Secret"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">短信签名</label>
            <input
              type="text"
              value={config.signName}
              onChange={(e) => setConfig({ ...config, signName: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="如: 编程优题库"
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            短信模板配置
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">验证码模板</label>
                <input
                  type="text"
                  value={config.verifyCodeTemplate}
                  onChange={(e) => setConfig({ ...config, verifyCodeTemplate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="如: SMS_506425088"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">教师审核通过模板</label>
                <input
                  type="text"
                  value={config.teacherApproveTemplate}
                  onChange={(e) => setConfig({ ...config, teacherApproveTemplate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="如: SMS_506060098"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">教师审核不通过模板</label>
                <input
                  type="text"
                  value={config.teacherRejectTemplate}
                  onChange={(e) => setConfig({ ...config, teacherRejectTemplate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="如: SMS_506065098"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            {saving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Send className="w-5 h-5 text-green-600" />
          发送测试短信
        </h3>
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Smartphone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg"
              placeholder="请输入测试手机号"
            />
          </div>
          <button
            onClick={handleTest}
            disabled={testing}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {testing ? '发送中...' : '发送测试'}
          </button>
        </div>
      </div>
    </div>
  );
}
